import type { Ref } from 'vue';

import { SEARCH_SUGGEST } from '~/constants';

/**
 * Manages typing activity state with debounce
 * Prevents flash of empty state while user is actively typing
 *
 * @param searchTerm - The reactive search term to watch
 * @param debounceMs - Debounce delay in milliseconds (default: SEARCH_SUGGEST.TYPING_DEBOUNCE_MS)
 * @param hasSelectedValue - Optional ref to check if a value is selected (prevents race conditions)
 * @returns isTyping state and cleanup function
 */
export function useTypingActivity(
	searchTerm: Ref<string>,
	debounceMs: number = SEARCH_SUGGEST.TYPING_DEBOUNCE_MS,
	hasSelectedValue?: Ref<boolean>
) {
	const isTyping = ref<boolean>(false);
	const typingTimeout = ref<NodeJS.Timeout>();

	// Watch for typing activity
	watch(searchTerm, () => {
		// Prevent race condition: if a selection is active, don't trigger typing state
		if (hasSelectedValue?.value) {
			return;
		}

		// Set typing flag immediately
		isTyping.value = true;

		// Clear existing timeout
		if (typingTimeout.value) {
			clearTimeout(typingTimeout.value);
		}

		// Reset typing flag after debounce delay
		typingTimeout.value = setTimeout(() => {
			isTyping.value = false;
		}, debounceMs);
	});

	// Watch hasSelectedValue to clear typing state on selection
	if (hasSelectedValue) {
		watch(hasSelectedValue, selected => {
			if (selected) {
				isTyping.value = false;
				if (typingTimeout.value) {
					clearTimeout(typingTimeout.value);
				}
			}
		});
	}

	// Cleanup on unmount
	onUnmounted(() => {
		if (typingTimeout.value) {
			clearTimeout(typingTimeout.value);
		}
	});

	return {
		isTyping: readonly(isTyping)
	};
}
